import { useState, useRef, useCallback, useEffect } from 'react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { User, Upload, X, Settings, Save, FolderOpen, Trash2, Theater } from 'lucide-react'
import { useConsultationStore } from '../../../../stores/consultationStore'
import type { RoleplaySettings } from '@creanizer/types'

// デバウンス用カスタムフック
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function UserSettings() {
  // 選択的サブスクリプション（パフォーマンス最適化）
  const updateUserSettings = useConsultationStore(state => state.updateUserSettings)
  const setRoleplaySettings = useConsultationStore(state => state.setRoleplaySettings)
  const updateRoleplaySettings = useConsultationStore(state => state.updateRoleplaySettings)
  const setUserIcon = useConsultationStore(state => state.setUserIcon)
  const saveUserSettingsPreset = useConsultationStore(state => state.saveUserSettingsPreset)
  const loadUserSettingsPreset = useConsultationStore(state => state.loadUserSettingsPreset)
  const deleteUserSettingsPreset = useConsultationStore(state => state.deleteUserSettingsPreset)

  const userSettings = useConsultationStore(state => state.userSettings) || {
    enabled: false,
    name: '',
    profile: ''
  }
  const userIcon = useConsultationStore(state => state.userIcon)
  const userSettingsPresets = useConsultationStore(state => state.userSettingsPresets)
  const roleplaySettings = useConsultationStore(state => state.roleplaySettings) || {
    enabled: false,
    location: '',
    situation: ''
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  // ローカルステート（入力中の値を保持）
  const [localSettings, setLocalSettings] = useState(userSettings)
  const [localRoleplay, setLocalRoleplay] = useState<RoleplaySettings>(roleplaySettings)
  const [showPresetSave, setShowPresetSave] = useState(false)
  const [presetName, setPresetName] = useState('')

  // デバウンスされた値（500ms後にストアを更新）
  const debouncedSettings = useDebounce(localSettings, 500)
  const debouncedRoleplay = useDebounce(localRoleplay, 500)

  // デバウンスされた値が変更されたらストアを更新
  useEffect(() => {
    if (localSettings.enabled) {
      updateUserSettings(debouncedSettings)
    }
  }, [debouncedSettings, updateUserSettings, localSettings.enabled])

  useEffect(() => {
    if (localRoleplay.enabled) {
      updateRoleplaySettings(debouncedRoleplay)
    }
  }, [debouncedRoleplay, updateRoleplaySettings, localRoleplay.enabled])

  // 有効/無効の切り替え（即座に反映）
  const handleEnabledChange = useCallback((checked: boolean) => {
    const newSettings = { ...localSettings, enabled: checked }
    setLocalSettings(newSettings)
    updateUserSettings(newSettings)
  }, [localSettings, updateUserSettings])

  // 名前の変更（ローカルのみ、デバウンスで反映）
  const handleNameChange = useCallback((name: string) => {
    setLocalSettings(prev => ({ ...prev, name }))
  }, [])

  // プロフィールの変更（ローカルのみ、デバウンスで反映）
  const handleProfileChange = useCallback((profile: string) => {
    setLocalSettings(prev => ({ ...prev, profile }))
  }, [])

  // フォーカスが外れた時に即座に保存
  const handleBlur = useCallback(() => {
    if (localSettings.enabled) {
      updateUserSettings(localSettings)
    }
  }, [localSettings, updateUserSettings])

  // ロールプレイ設定の変更
  const handleRoleplayEnabledChange = useCallback((checked: boolean) => {
    const newRoleplay = { ...localRoleplay, enabled: checked }
    setLocalRoleplay(newRoleplay)
    setRoleplaySettings(newRoleplay)
  }, [localRoleplay, setRoleplaySettings])

  const handleRoleplayLocationChange = useCallback((location: string) => {
    setLocalRoleplay(prev => ({ ...prev, location }))
  }, [])

  const handleRoleplaySituationChange = useCallback((situation: string) => {
    setLocalRoleplay(prev => ({ ...prev, situation }))
  }, [])

  // ロールプレイ設定のフォーカスが外れた時に即座に保存
  const handleRoleplayBlur = useCallback(() => {
    if (localRoleplay.enabled) {
      updateRoleplaySettings(localRoleplay)
    }
  }, [localRoleplay, updateRoleplaySettings])

  // アイコンのアップロード
  const handleIconUpload = useCallback((file: File) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      const base64 = event.target?.result as string
      setUserIcon(base64)
    }
    reader.readAsDataURL(file)
  }, [setUserIcon])

  // アイコンの削除
  const handleIconRemove = useCallback(() => {
    setUserIcon(null)
  }, [setUserIcon])

  // プリセット保存
  const handleSavePreset = useCallback(() => {
    if (!presetName.trim()) return

    const preset = saveUserSettingsPreset(presetName.trim())
    if (preset) {
      setPresetName('')
      setShowPresetSave(false)
    }
  }, [presetName, saveUserSettingsPreset])

  // プリセット読み込み
  const handleLoadPreset = useCallback((presetId: string) => {
    const success = loadUserSettingsPreset(presetId)
    if (success) {
      // ストアから最新の設定を取得してローカルステートを更新
      // （loadUserSettingsPresetがストアを更新するので、次のレンダリングで反映される）
      const store = useConsultationStore.getState()
      if (store.userSettings) {
        setLocalSettings(store.userSettings)
      }
      if (store.roleplaySettings) {
        setLocalRoleplay(store.roleplaySettings)
      } else {
        setLocalRoleplay({ enabled: false, location: '', situation: '' })
      }
    }
  }, [loadUserSettingsPreset])

  // プリセット削除
  const handleDeletePreset = useCallback((presetId: string) => {
    deleteUserSettingsPreset(presetId)
  }, [deleteUserSettingsPreset])

  return (
    <div className="space-y-4">
      {/* ユーザー設定の有効化 */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enableUserSettings"
          checked={localSettings.enabled}
          onCheckedChange={handleEnabledChange}
        />
        <Label htmlFor="enableUserSettings" className="text-sm cursor-pointer">
          <Settings className="inline-block w-4 h-4 mr-1" />
          ユーザー設定を有効にする
        </Label>
      </div>

      {localSettings.enabled && (
        <>
          {/* アイコン設定 */}
          <div className="space-y-2">
            <Label className="text-xs">アイコン</Label>
            <div className="flex items-center gap-3">
              <div className="relative group">
                {userIcon ? (
                  <img
                    src={userIcon}
                    alt="User Icon"
                    className="w-16 h-16 rounded-full object-cover border-2 border-border"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                    <User className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                {/* ホバー時のオーバーレイ */}
                <div className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleIconUpload(file)
                    }}
                  />
                  <label htmlFor={fileInputRef.current?.id} className="cursor-pointer">
                    <Upload
                      className="h-5 w-5 text-white cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    />
                  </label>
                  {userIcon && (
                    <X
                      className="h-5 w-5 text-white cursor-pointer"
                      onClick={handleIconRemove}
                    />
                  )}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                クリックまたはドラッグ＆ドロップで画像をアップロード
              </div>
            </div>
          </div>

          {/* 名前設定 */}
          <div className="space-y-2">
            <Label htmlFor="userName" className="text-xs">
              名前（空欄の場合は「あなた」と表示）
            </Label>
            <Input
              id="userName"
              type="text"
              value={localSettings.name || ''}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={handleBlur}
              placeholder="例：山田太郎"
              className="h-8 text-sm"
            />
          </div>

          {/* プロフィール設定 */}
          <div className="space-y-2">
            <Label htmlFor="userProfile" className="text-xs">
              プロフィール（任意・自由記述）
            </Label>
            <Textarea
              id="userProfile"
              value={localSettings.profile || ''}
              onChange={(e) => handleProfileChange(e.target.value)}
              onBlur={handleBlur}
              placeholder="例：小説家を目指している大学生です。ファンタジーやSFが好きで、特に世界観設定にこだわりたいと思っています。"
              className="text-sm resize-vertical min-h-[80px]"
              rows={3}
            />
            <div className="text-xs text-muted-foreground">
              AIがあなたのことを理解して、より適切なアドバイスができるようになります
            </div>
          </div>

          {/* ロールプレイ設定 */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enableRoleplay"
                checked={localRoleplay.enabled}
                onCheckedChange={handleRoleplayEnabledChange}
              />
              <Label htmlFor="enableRoleplay" className="text-sm cursor-pointer">
                <Theater className="inline-block w-4 h-4 mr-1" />
                ロールプレイモード
              </Label>
            </div>

            {localRoleplay.enabled && (
              <div className="space-y-3 ml-6">
                <div className="text-xs text-muted-foreground">
                  相談ではなく、キャラクターとの場面を演じるモードです
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roleplayLocation" className="text-xs">
                    場所・時間
                  </Label>
                  <Input
                    id="roleplayLocation"
                    type="text"
                    value={localRoleplay.location || ''}
                    onChange={(e) => handleRoleplayLocationChange(e.target.value)}
                    onBlur={handleRoleplayBlur}
                    placeholder="例：夜の喫茶店、学校の屋上（放課後）、雨の日の図書館"
                    className="h-8 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roleplaySituation" className="text-xs">
                    シチュエーション
                  </Label>
                  <Textarea
                    id="roleplaySituation"
                    value={localRoleplay.situation || ''}
                    onChange={(e) => handleRoleplaySituationChange(e.target.value)}
                    onBlur={handleRoleplayBlur}
                    placeholder="例：悩みを打ち明けられた、偶然再会した、喧嘩の後で気まずい"
                    className="text-sm resize-vertical min-h-[60px]"
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          {/* プリセット管理 */}
          <div className="space-y-2 border-t pt-4">
            <Label className="text-xs font-semibold flex items-center gap-1">
              <FolderOpen className="h-3 w-3" />
              プリセット
            </Label>

            {/* プリセット読み込み */}
            {userSettingsPresets.length > 0 && (
              <div className="space-y-1">
                {userSettingsPresets.map(preset => (
                  <div key={preset.id} className="flex items-center justify-between p-2 border rounded text-xs">
                    <div className="flex items-center gap-2 flex-1">
                      {preset.icon ? (
                        <img
                          src={preset.icon}
                          alt={preset.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                          <User className="h-3 w-3 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-1">
                          {preset.name}
                          {preset.roleplaySettings?.enabled && (
                            <span title="ロールプレイ設定あり">
                              <Theater className="h-3 w-3 text-muted-foreground" />
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground truncate max-w-[150px]">
                          {preset.settings.name || '名前未設定'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleLoadPreset(preset.id)}
                        title="読み込み"
                        className="h-7 px-2"
                      >
                        <FolderOpen className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePreset(preset.id)}
                        title="削除"
                        className="h-7 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* プリセット保存 */}
            {(localSettings.name || localSettings.profile) && (
              showPresetSave ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="プリセット名"
                    className="flex-1 h-8 text-xs"
                    onKeyPress={(e) => e.key === 'Enter' && handleSavePreset()}
                  />
                  <Button
                    size="sm"
                    onClick={handleSavePreset}
                    disabled={!presetName.trim()}
                    className="h-8"
                  >
                    <Save className="h-3 w-3 mr-1" />
                    保存
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowPresetSave(false)
                      setPresetName('')
                    }}
                    className="h-8"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPresetSave(true)}
                  className="w-full h-8 text-xs"
                >
                  <Save className="h-3 w-3 mr-1" />
                  現在の設定をプリセットとして保存
                </Button>
              )
            )}

            {userSettingsPresets.length === 0 && !(localSettings.name || localSettings.profile) && (
              <div className="text-xs text-muted-foreground p-2 text-center">
                名前またはプロフィールを設定するとプリセット保存が可能です
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
